import React, { useState } from 'react';
import { useMembership } from '../../context/MembershipContext';
import { FaCrown, FaUser, FaInfoCircle, FaArrowUp } from 'react-icons/fa';

const MembershipStatus = ({ showUpgradeButton = true, className = '', showUserInfo = true }) => {
  const { 
    membershipStatus, 
    loading, 
    error, 
    isPremium, 
    isFree,
    availablePlans,
    userInfo,
    isNewMembership
  } = useMembership();
  
  const [showDetails, setShowDetails] = useState(false);

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border p-4 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-center text-red-600">
          <FaInfoCircle className="mr-2" />
          <span className="text-sm">Error al cargar estado de membresía</span>
        </div>
      </div>
    );
  }

  if (!membershipStatus) {
    return null;
  }

  const { plan, features = [], expiresAt } = membershipStatus;
  const isExpired = expiresAt && new Date(expiresAt) < new Date();

  return (
    <div className={`bg-white rounded-lg shadow-sm border p-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          {isPremium ? (
            <FaCrown className="text-yellow-500 text-xl mr-2" />
          ) : (
            <FaUser className="text-gray-500 text-xl mr-2" />
          )}
          <div>
            <h3 className="font-semibold text-gray-900">
              {isPremium ? 'Plan Premium' : 'Plan Gratuito'}
            </h3>
            <p className="text-sm text-gray-500">
              {isPremium ? 'Acceso completo a todas las características' : 'Características básicas'}
            </p>
          </div>
        </div>
        
        {showUpgradeButton && isFree && (
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center text-sm text-blue-600 hover:text-blue-700 transition-colors"
          >
            <FaArrowUp className="mr-1" />
            Ver planes
          </button>
        )}
      </div>

      {/* Status Badge */}
      <div className="mb-3">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          isPremium 
            ? 'bg-yellow-100 text-yellow-800' 
            : 'bg-gray-100 text-gray-800'
        }`}>
          {isPremium ? 'Premium' : 'Gratuito'}
          {isExpired && (
            <span className="ml-1 text-red-600">(Expirado)</span>
          )}
          {isNewMembership && (
            <span className="ml-1 text-green-600">(Nuevo)</span>
          )}
        </span>
      </div>

      {/* User Info */}
      {showUserInfo && userInfo && (
        <div className="mb-3 p-3 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-1">Información del Usuario</h4>
          <div className="text-sm text-gray-600 space-y-1">
            <p><strong>Nombre:</strong> {userInfo.fullName}</p>
            <p><strong>Email:</strong> {userInfo.email}</p>
            {userInfo.cellNumber && (
              <p><strong>Teléfono:</strong> {userInfo.cellNumber}</p>
            )}
            {userInfo.service && (
              <p><strong>Servicio:</strong> {userInfo.service}</p>
            )}
          </div>
        </div>
      )}

      {/* Features List */}
      {showDetails && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h4 className="font-medium text-gray-900 mb-2">Características disponibles:</h4>
          <ul className="space-y-1">
            {features.length > 0 ? (
              features.map((feature, index) => (
                <li key={index} className="flex items-center text-sm text-gray-600">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></div>
                  {feature}
                </li>
              ))
            ) : (
              <li className="text-sm text-gray-500 italic">
                Características básicas incluidas
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Expiration Info */}
      {expiresAt && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            {isExpired ? 'Expiró el' : 'Expira el'}: {new Date(expiresAt).toLocaleDateString('es-ES')}
          </p>
        </div>
      )}

      {/* Upgrade CTA for Free Users */}
      {showUpgradeButton && isFree && availablePlans.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-2">
              ¿Quieres acceso completo a todas las características?
            </p>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FaCrown className="mr-2" />
              Actualizar a Premium
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembershipStatus; 